import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEOHead from '../../components/SEOHead';

export default function ServicesPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBookService = (serviceName: string) => {
    setSelectedService(serviceName);
    setShowBookingForm(true);
    document.body.style.overflow = 'hidden';
  };

  const closeBookingForm = () => {
    setShowBookingForm(false);
    setSelectedService('');
    setFormStatus('idle');
    document.body.style.overflow = 'auto';
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    formData.append('access_key', 'd8a90d32-fad8-4c33-b2f6-b27dc380d5fc');
    formData.append('subject', `Service Booking: ${selectedService}`);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => {
          closeBookingForm();
        }, 2000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  const baseUrl = import.meta.env.VITE_SITE_URL || 'https://jackspumps.ie';

  const servicesSchema = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${baseUrl}/services#webpage`,
      "url": `${baseUrl}/services`,
      "name": "Pump Station Services Dublin & Leinster | Maintenance, Repairs & Installation | Jack's Pumps",
      "description": "Professional pump station services across Dublin, Leinster & Ireland including maintenance, emergency repairs, installation, diagnostics, and de-ragging. Serving Dublin, Kildare, Meath, Wicklow, Wexford, Louth, and Carlow. 24/7 emergency response available.",
      "isPartOf": {"@id": `${baseUrl}/#website`},
      "about": {"@id": `${baseUrl}/#organization`}
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": `${baseUrl}/`
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Services",
          "item": `${baseUrl}/services`
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Pump Station Services by Jack's Pumps",
      "description": "Professional pump station services across Dublin, Leinster and Ireland",
      "numberOfItems": 7,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "Service",
            "name": "Foul Pump Station Servicing & Maintenance",
            "description": "Regular servicing and preventative maintenance to keep your pump stations running reliably",
            "url": `${baseUrl}/services`,
            "provider": {"@id": `${baseUrl}/#organization`},
            "areaServed": [
              {"@type": "City", "name": "Dublin"},
              {"@type": "AdministrativeArea", "name": "Leinster"},
              {"@type": "Country", "name": "Ireland"}
            ]
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@type": "Service",
            "name": "Emergency Pump Repairs & 24/7 Callouts",
            "description": "24/7 emergency response for pump failures and urgent breakdowns across Dublin and Leinster",
            "url": `${baseUrl}/services`,
            "provider": {"@id": `${baseUrl}/#organization`},
            "areaServed": [
              {"@type": "City", "name": "Dublin"},
              {"@type": "AdministrativeArea", "name": "Leinster"},
              {"@type": "Country", "name": "Ireland"}
            ]
          }
        },
        {
          "@type": "ListItem",
          "position": 3,
          "item": {
            "@type": "Service",
            "name": "Pump Installation & Replacement",
            "description": "Professional installation of new pump systems and replacement of failed equipment across Ireland",
            "url": `${baseUrl}/services`,
            "provider": {"@id": `${baseUrl}/#organization`},
            "areaServed": [
              {"@type": "City", "name": "Dublin"},
              {"@type": "AdministrativeArea", "name": "Leinster"},
              {"@type": "Country", "name": "Ireland"}
            ]
          }
        },
        {
          "@type": "ListItem",
          "position": 4,
          "item": {
            "@type": "Service",
            "name": "Pump Fault Finding & Diagnostics",
            "description": "Expert diagnosis of pump system issues using professional testing equipment",
            "url": `${baseUrl}/services`,
            "provider": {"@id": `${baseUrl}/#organization`},
            "areaServed": [
              {"@type": "City", "name": "Dublin"},
              {"@type": "AdministrativeArea", "name": "Leinster"},
              {"@type": "Country", "name": "Ireland"}
            ]
          }
        },
        {
          "@type": "ListItem",
          "position": 5,
          "item": {
            "@type": "Service",
            "name": "Pump De-ragging, Unblocking & Clean-outs",
            "description": "Clearing blockages, removing debris, and cleaning pump chambers to restore full operation",
            "url": `${baseUrl}/services`,
            "provider": {"@id": `${baseUrl}/#organization`},
            "areaServed": [
              {"@type": "City", "name": "Dublin"},
              {"@type": "AdministrativeArea", "name": "Leinster"},
              {"@type": "Country", "name": "Ireland"}
            ]
          }
        },
        {
          "@type": "ListItem",
          "position": 6,
          "item": {
            "@type": "Service",
            "name": "Stormwater & Surface Water Pump Systems",
            "description": "Installation, servicing and repair of surface water and stormwater pumping systems",
            "url": `${baseUrl}/services`,
            "provider": {"@id": `${baseUrl}/#organization`},
            "areaServed": [
              {"@type": "City", "name": "Dublin"},
              {"@type": "AdministrativeArea", "name": "Leinster"},
              {"@type": "Country", "name": "Ireland"}
            ]
          }
        },
        {
          "@type": "ListItem",
          "position": 7,
          "item": {
            "@type": "Service",
            "name": "Planned Pump Service Contracts for Managed Sites",
            "description": "Scheduled maintenance programs tailored to your site requirements and compliance needs",
            "url": `${baseUrl}/services`,
            "provider": {"@id": `${baseUrl}/#organization`},
            "areaServed": [
              {"@type": "City", "name": "Dublin"},
              {"@type": "AdministrativeArea", "name": "Leinster"},
              {"@type": "Country", "name": "Ireland"}
            ]
          }
        }
      ]
    }
  ];

  return (
    <>
      <SEOHead
        title="Pump Station Services Dublin & Leinster | Maintenance, Repairs & Installation | Jack's Pumps"
        description="Professional pump station services across Dublin, Leinster & Ireland including maintenance, emergency repairs, installation, diagnostics, and de-ragging. Serving Dublin, Kildare, Meath, Wicklow, Wexford, Louth, and Carlow. 24/7 emergency response available."
        canonical="/services"
        keywords="pump station services Dublin, pump repairs Dublin, pump maintenance Dublin, pump services Leinster, emergency pump repairs Dublin, pump installation Dublin, pump diagnostics Dublin, de-ragging services Dublin, pump repairs Kildare, pump repairs Meath, pump repairs Wicklow, pump station Leinster, 24/7 pump repairs Dublin"
        ogTitle="Pump Station Services Ireland | Jack's Pumps"
        ogDescription="Professional pump station maintenance, emergency repairs, and installation services across Ireland. 24/7 emergency response available."
        schema={servicesSchema}
      />
      
      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-white shadow-md'}`} role="navigation" aria-label="Main navigation">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20 lg:h-24 py-2">
              <Link to="/" className="flex items-center -ml-2 sm:-ml-4 lg:ml-0" aria-label="Jack's Pumps Home">
                <img 
                  src="https://static.readdy.ai/image/dc999f92be6fe23ab0f2c081255e30e9/be41bd6c16e3f7ca48ba1bf859508677.jpeg" 
                  alt="Jack's Pumps Logo - Professional Pump Station Services Ireland" 
                  className="h-16 sm:h-20 lg:h-20 w-auto"
                  width="120"
                  height="80"
                />
              </Link>
              <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
                <Link to="/" className="text-base lg:text-base font-medium text-gray-900 hover:text-[#00D084] transition-colors">Home</Link>
                <Link to="/services" className="text-base lg:text-base font-medium text-[#00D084] transition-colors">Services</Link>
                <Link to="/contact" className="text-base lg:text-base font-medium text-gray-900 hover:text-[#00D084] transition-colors">Contact</Link>
                <a href="tel:+35340454753" className="bg-[#00D084] text-white px-6 lg:px-8 py-3 lg:py-3.5 rounded-lg font-medium hover:bg-[#00B870] transition-colors whitespace-nowrap text-base" aria-label="Call Jack's Pumps for emergency pump repairs">
                  Call Now
                </a>
              </div>
              <div className="md:hidden">
                <a href="tel:+35340454753" className="bg-[#00D084] text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap" aria-label="Call Jack's Pumps">
                  Call
                </a>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <header className="relative pt-32 lg:pt-40 pb-20 lg:pb-28 bg-[#1A1A1A]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-block bg-[#00D084]/20 border border-[#00D084] px-4 lg:px-6 py-2 lg:py-3 rounded-full mb-6">
              <span className="text-[#00D084] text-sm lg:text-base font-medium uppercase tracking-wide">Nationwide Coverage</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white mb-6 lg:mb-8 leading-tight">
              <strong>Pump Station Services Across Ireland</strong>
            </h1>
            
            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-gray-300 mb-10 max-w-4xl mx-auto">
              Professional <strong>pump station maintenance</strong>, <strong>emergency repairs</strong>, and <strong>installation services</strong> across <strong>Dublin, Wicklow, Wexford, Kildare, Meath, Louth, and Carlow</strong>
            </p>
          </div>
        </header>

        {/* Services Grid */}
        <main>
          <section className="py-20 lg:py-28 bg-[#F5F5F5]" aria-labelledby="services-list-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 id="services-list-heading" className="sr-only">Complete List of Pump Services</h2>
              <div className="space-y-8 lg:space-y-10">
                {[
                  {
                    num: '01',
                    title: 'Foul Pump Station Servicing & Maintenance',
                    desc: 'Regular servicing and preventative maintenance to keep your pump stations running reliably. We provide scheduled inspections, cleaning, testing, and component replacement to prevent failures and extend system life across Ireland.',
                    best: 'Commercial sites, housing estates, managed properties',
                    features: ['Scheduled inspections', 'Preventative maintenance', 'Component testing', 'Performance optimization', 'Compliance checks'],
                    img: 'https://public.readdy.ai/ai/img_res/b40aff31d9c0774e6e55667bef086b66.jpg'
                  },
                  {
                    num: '02',
                    title: 'Emergency Pump Repairs & Callouts',
                    desc: 'Fast response to pump failures, alarms, and urgent breakdowns across Ireland. Available 24/7 for emergency situations requiring immediate attention to prevent flooding or system damage in Dublin, Wicklow, Wexford, and surrounding areas.',
                    best: 'Emergency situations, pump failures, alarm callouts',
                    features: ['24/7 availability', 'Rapid response', 'Emergency diagnostics', 'On-site repairs', 'Priority service'],
                    img: 'https://readdy.ai/api/search-image?query=Emergency%20response%20service%20vehicle%20arriving%20at%20industrial%20pump%20site%20at%20night%20with%20flashing%20amber%20lights%2C%20technician%20with%20emergency%20toolkit%20and%20safety%20gear%20rushing%20toward%20pump%20chamber%20entrance%2C%20dramatic%20urgent%20atmosphere%20with%20red%20alarm%20warning%20indicators%20and%20green%20safety%20lights%20visible%2C%20professional%20rapid%20response%20emergency%20service%20photography%20showing%20immediate%20action%20capability&width=640&height=480&seq=service-02-emergency&orientation=landscape'
                  },
                  {
                    num: '03',
                    title: 'Pump Installation & Replacement',
                    desc: 'Professional installation of new pump systems and replacement of failed equipment. We handle everything from specification and supply to installation, commissioning, and handover across Ireland.',
                    best: 'New builds, system upgrades, pump replacements',
                    features: ['System design', 'Equipment supply', 'Professional installation', 'Commissioning', 'Documentation'],
                    img: 'https://readdy.ai/api/search-image?query=Active%20construction%20site%20showing%20brand%20new%20submersible%20pump%20being%20carefully%20lowered%20into%20freshly%20built%20concrete%20pump%20chamber%20using%20professional%20lifting%20equipment%20and%20chains%2C%20modern%20installation%20with%20new%20piping%20systems%20and%20electrical%20conduits%20clearly%20visible%2C%20bright%20daylight%20construction%20photography%20with%20safety%20barriers%20and%20hard%20hats%2C%20professional%20installation%20team%20coordinating%20work%2C%20green%20equipment%20manufacturer%20accents%20on%20new%20pump&width=640&height=480&seq=service-03-installation&orientation=landscape'
                  },
                  {
                    num: '04',
                    title: 'Fault Finding & Diagnostics',
                    desc: 'Expert diagnosis of pump system issues using professional testing equipment. We identify root causes of problems including electrical faults, mechanical issues, and control system failures.',
                    best: 'Intermittent faults, performance issues, system troubleshooting',
                    features: ['Electrical testing', 'Mechanical inspection', 'Control system diagnostics', 'Performance analysis', 'Detailed reporting'],
                    img: 'https://readdy.ai/api/search-image?query=Detailed%20close-up%20photograph%20of%20technician%20hands%20using%20advanced%20digital%20multimeter%20and%20diagnostic%20tablet%20computer%20to%20test%20electrical%20connections%20on%20industrial%20pump%20control%20panel%2C%20intricate%20technical%20work%20with%20circuit%20boards%20and%20color-coded%20wiring%20clearly%20visible%2C%20professional%20electronic%20testing%20equipment%20with%20bright%20LED%20displays%20showing%20diagnostic%20readings%2C%20focused%20technical%20expertise%20photography%20with%20green%20indicator%20status%20lights%20and%20charcoal%20control%20cabinet&width=640&height=480&seq=service-04-diagnostics&orientation=landscape'
                  },
                  {
                    num: '05',
                    title: 'De-ragging, Unblocking & Clean-outs',
                    desc: 'Clearing blockages, removing debris, and cleaning pump chambers to restore full operation. We handle everything from simple de-ragging to complete chamber clean-outs and system flushing.',
                    best: 'Blocked pumps, reduced flow, debris buildup',
                    features: ['Pump de-ragging', 'Chamber cleaning', 'Blockage removal', 'System flushing', 'Preventative advice'],
                    img: 'https://readdy.ai/api/search-image?query=Industrial%20pump%20technician%20wearing%20full%20protective%20gear%20including%20respirator%20and%20waterproof%20suit%20using%20specialized%20high-pressure%20cleaning%20equipment%20inside%20confined%20space%20pump%20chamber%2C%20powerful%20water%20jets%20removing%20accumulated%20debris%20from%20large%20pump%20impeller%2C%20intensive%20industrial%20cleaning%20operation%20showing%20dramatic%20before%20and%20after%20transformation%2C%20professional%20confined%20space%20entry%20safety%20equipment%20and%20procedures%20clearly%20visible%2C%20atmospheric%20lighting%20showing%20thorough%20deep%20cleaning%20maintenance%20work&width=640&height=480&seq=service-05-cleaning&orientation=landscape'
                  },
                  {
                    num: '06',
                    title: 'Stormwater & Surface Water Pump Systems',
                    desc: 'Installation, servicing and repair of surface water and stormwater pumping systems. We work with drainage systems, flood prevention equipment, and surface water management infrastructure.',
                    best: 'Drainage systems, flood prevention, surface water management',
                    features: ['System installation', 'Flood prevention', 'Regular servicing', 'Emergency repairs', 'Capacity upgrades'],
                    img: 'https://readdy.ai/api/search-image?query=Large%20outdoor%20stormwater%20pumping%20station%20facility%20with%20multiple%20high-capacity%20industrial%20pumps%20and%20modern%20automated%20control%20systems%20cabinet%2C%20extensive%20surface%20water%20drainage%20infrastructure%20with%20metal%20grated%20inlets%20and%20collection%20channels%20visible%20in%20foreground%2C%20professional%20installation%20integrated%20into%20landscaped%20commercial%20property%20setting%20with%20maintained%20grounds%2C%20clear%20blue%20sky%20background%20emphasizing%20flood%20prevention%20and%20water%20management%20system%2C%20bright%20green%20status%20indicator%20lights%20on%20weatherproof%20control%20cabinet%20showing%20system%20operational&width=640&height=480&seq=service-06-stormwater&orientation=landscape'
                  },
                  {
                    num: '07',
                    title: 'Planned Service Contracts for Managed Sites',
                    desc: 'Scheduled maintenance programs tailored to your site requirements and compliance needs. We provide regular visits, comprehensive reporting, and priority emergency response for contract customers.',
                    best: 'Property managers, facilities teams, multi-site operations',
                    features: ['Scheduled visits', 'Comprehensive reporting', 'Priority response', 'Budget certainty', 'Compliance support'],
                    img: 'https://readdy.ai/api/search-image?query=Professional%20facilities%20manager%20in%20business%20attire%20reviewing%20comprehensive%20digital%20maintenance%20schedule%20on%20tablet%20device%20with%20uniformed%20service%20technician%20at%20modern%20commercial%20building%20pump%20room%20entrance%2C%20organized%20service%20documentation%20binders%20and%20compliance%20certificates%20mounted%20on%20wall%20in%20background%2C%20clean%20well-maintained%20pump%20equipment%20showing%20visible%20results%20of%20planned%20preventative%20maintenance%20program%2C%20professional%20business%20relationship%20photography%20with%20green%20operational%20status%20displays%20and%20charcoal%20industrial%20equipment&width=640&height=480&seq=service-07-contracts&orientation=landscape'
                  }
                ].map((service, index) => (
                  <article key={index} className="bg-white border-2 border-[#1A1A1A] rounded-2xl overflow-hidden hover:shadow-2xl transition-all">
                    <div className="flex flex-col lg:flex-row">
                      <div className="lg:w-2/5 h-64 sm:h-80 lg:h-96">
                        <img 
                          src={service.img}
                          alt={`${service.title} - Professional pump services in Dublin, Wicklow, Wexford, Kildare, Meath, Louth, and Carlow`}
                          title={`${service.title} - Jack's Pumps Ireland`}
                          className="w-full h-full object-cover object-top"
                          width="640"
                          height="480"
                          loading="lazy"
                        />
                      </div>
                      <div className="lg:w-3/5 p-6 sm:p-8 lg:p-10">
                        <div className="w-10 h-10 lg:w-12 lg:h-12 bg-[#00D084] rounded-full flex items-center justify-center mb-4">
                          <span className="text-white font-bold text-lg lg:text-xl">{service.num}</span>
                        </div>
                        <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#1A1A1A] mb-3 lg:mb-4">
                          <strong>{service.title}</strong>
                        </h3>
                        <p className="text-sm sm:text-base lg:text-lg text-gray-600 mb-4 lg:mb-6 leading-relaxed">{service.desc}</p>
                        
                        <div className="flex items-start gap-2 mb-6 lg:mb-8">
                          <i className="ri-checkbox-circle-fill text-[#00D084] text-xl lg:text-2xl mt-0.5" aria-hidden="true"></i>
                          <p className="text-sm lg:text-base text-gray-700"><span className="font-semibold">Best for:</span> {service.best}</p>
                        </div>

                        <div className="mb-6 lg:mb-8">
                          <h4 className="font-semibold text-base lg:text-lg text-[#1A1A1A] mb-3">What's Included:</h4>
                          <ul className="space-y-2">
                            {service.features.map((feature, idx) => (
                              <li key={idx} className="flex items-center gap-2 text-sm lg:text-base text-gray-600">
                                <i className="ri-check-line text-[#00D084] text-lg lg:text-xl" aria-hidden="true"></i>
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="flex gap-3 lg:gap-4">
                          <button 
                            onClick={() => handleBookService(service.title)}
                            className="bg-[#00D084] text-white px-5 lg:px-8 py-3 lg:py-4 rounded-lg font-medium text-sm lg:text-base hover:bg-[#00B870] transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                            aria-label={`Book ${service.title} service`}
                          >
                            Book Service
                            <i className="ri-calendar-check-fill" aria-hidden="true"></i>
                          </button>
                          <Link to="/contact" className="bg-[#1A1A1A] text-white px-5 lg:px-8 py-3 lg:py-4 rounded-lg font-medium text-sm lg:text-base hover:bg-[#2A2A2A] transition-colors whitespace-nowrap" aria-label={`Get quote for ${service.title}`}>
                            Get Quote
                          </Link>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </main>

        {/* Booking Form Modal */}
        {showBookingForm && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl max-w-2xl w-full my-8 relative">
              <button 
                onClick={closeBookingForm}
                className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-gray-700"></i>
              </button>

              <div className="p-8">
                <div className="mb-6">
                  <div className="w-12 h-12 bg-[#00D084] rounded-full flex items-center justify-center mb-4">
                    <i className="ri-calendar-check-fill text-2xl text-white"></i>
                  </div>
                  <h2 className="text-3xl font-bold text-[#1A1A1A] mb-2">Book Your Service</h2>
                  <p className="text-gray-600">Fill out the form below and we'll get back to you shortly</p>
                </div>

                {formStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                    <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                    <p className="text-green-800 font-medium">Booking submitted successfully! We'll contact you soon.</p>
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                    <i className="ri-error-warning-fill text-2xl text-red-600"></i>
                    <p className="text-red-800 font-medium">Something went wrong. Please try again or call us directly.</p>
                  </div>
                )}

                <form id="service-booking-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <input type="hidden" name="service" value={selectedService} />

                  <div>
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Service Selected</label>
                    <div className="p-3 bg-[#00D084]/10 border border-[#00D084] rounded-lg">
                      <p className="text-[#1A1A1A] font-medium">{selectedService}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-[#1A1A1A] mb-2">Full Name *</label>
                      <input 
                        type="text" 
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#00D084] focus:outline-none transition-colors text-sm"
                        placeholder="John Smith"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-[#1A1A1A] mb-2">Phone Number *</label>
                      <input 
                        type="tel" 
                        id="phone"
                        name="phone"
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#00D084] focus:outline-none transition-colors text-sm"
                        placeholder="+353 87 123 4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-[#1A1A1A] mb-2">Email Address *</label>
                    <input 
                      type="email" 
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#00D084] focus:outline-none transition-colors text-sm"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="location" className="block text-sm font-semibold text-[#1A1A1A] mb-2">Location *</label>
                    <input 
                      type="text" 
                      id="location"
                      name="location"
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#00D084] focus:outline-none transition-colors text-sm"
                      placeholder="Dublin, Wicklow, etc."
                    />
                  </div>

                  <div>
                    <label htmlFor="urgency" className="block text-sm font-semibold text-[#1A1A1A] mb-2">Urgency Level *</label>
                    <select 
                      id="urgency"
                      name="urgency"
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#00D084] focus:outline-none transition-colors text-sm cursor-pointer"
                    >
                      <option value="">Select urgency level</option>
                      <option value="emergency">Emergency - Immediate Response Needed</option>
                      <option value="urgent">Urgent - Within 24 Hours</option>
                      <option value="standard">Standard - Within 2-3 Days</option>
                      <option value="scheduled">Scheduled - Plan for Later</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-[#1A1A1A] mb-2">Additional Details</label>
                    <textarea 
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#00D084] focus:outline-none transition-colors text-sm resize-none"
                      placeholder="Please provide any additional information about your pump system or the issue you're experiencing... (Max 500 characters)"
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button 
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="flex-1 bg-[#00D084] text-white px-6 py-4 rounded-lg font-bold hover:bg-[#00B870] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'submitting' ? 'Submitting...' : 'Submit Booking Request'}
                    </button>
                    <button 
                      type="button"
                      onClick={closeBookingForm}
                      className="px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Service Areas */}
        <section className="py-20 bg-[#1A1A1A]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">Service Coverage</h2>
              <p className="text-gray-400 text-lg">Serving the East Coast of Ireland</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {['Dublin', 'Wicklow', 'Wexford', 'Kildare', 'Meath', 'Louth', 'Carlow', 'Surrounding Areas'].map((area, index) => (
                <div key={index} className="bg-[#2A2A2A] p-6 rounded-xl text-center hover:bg-[#00D084] transition-all group cursor-pointer">
                  <i className="ri-map-pin-fill text-4xl text-[#00D084] group-hover:text-white mb-3"></i>
                  <h3 className="text-white font-semibold">{area}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-[#00B870] to-[#00D084] py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
            <p className="text-white/90 text-lg mb-8">Contact us today for emergency repairs or to discuss your requirements</p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+35340454753" className="bg-white text-[#00D084] px-10 py-5 rounded-xl font-bold text-xl hover:shadow-2xl transition-all flex items-center justify-center gap-3 whitespace-nowrap">
                <i className="ri-phone-fill text-2xl"></i>
                Call Now
              </a>
              <Link to="/contact" className="bg-transparent border-2 border-white text-white px-10 py-5 rounded-xl font-bold text-xl hover:bg-white hover:text-[#00D084] transition-all flex items-center justify-center gap-3 whitespace-nowrap">
                Get a Quote
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
              <div>
                <img 
                  src="https://static.readdy.ai/image/dc999f92be6fe23ab0f2c081255e30e9/be41bd6c16e3f7ca48ba1bf859508677.jpeg" 
                  alt="Jack's Pumps" 
                  className="h-40 w-auto mb-4 bg-white rounded-lg p-2"
                />
                <p className="text-[#00D084] text-sm mb-3">Pump Station Experts • Nationwide Coverage</p>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Professional pump station servicing, repairs, and installations across Ireland.
                </p>
              </div>

              <div>
                <h3 className="text-white text-lg font-bold mb-4">Quick Links</h3>
                <div className="space-y-3">
                  <Link to="/" className="block text-gray-400 hover:text-[#00D084] transition-colors">Home</Link>
                  <Link to="/services" className="block text-gray-400 hover:text-[#00D084] transition-colors">Services</Link>
                  <Link to="/contact" className="block text-gray-400 hover:text-[#00D084] transition-colors">Contact</Link>
                </div>
              </div>

              <div>
                <h3 className="text-white text-lg font-bold mb-4">Contact</h3>
                <a href="tel:+35340454753" className="block text-white text-xl font-semibold mb-2 hover:text-[#00D084] transition-colors">
                  +353 404 54753
                </a>
                <a href="mailto:info@jackspumps.ie" className="block text-gray-400 mb-6 hover:text-[#00D084] transition-colors">
                  info@jackspumps.ie
                </a>
                
                <h4 className="text-white font-semibold mb-2">Service Area</h4>
                <p className="text-gray-400 text-sm">Nationwide Coverage</p>
              </div>
            </div>

            <div className="border-t border-[#00D084] pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex gap-4">
                  <a href="#" className="text-white hover:text-[#00D084] transition-colors">
                    <i className="ri-linkedin-fill text-2xl"></i>
                  </a>
                  <a href="#" className="text-white hover:text-[#00D084] transition-colors">
                    <i className="ri-facebook-fill text-2xl"></i>
                  </a>
                </div>
                
                <p className="text-gray-400 text-sm">© 2025 Jack's Pumps. All rights reserved.</p>
                
                <div className="flex gap-4 text-sm">
                  <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#00D084] transition-colors whitespace-nowrap">
                    Website Builder
                  </a>
                </div>
              </div>
            </div>
          </div>
        </footer>

        {/* Sticky Mobile Contact Bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-[#1A1A1A] border-t-2 border-[#00D084] shadow-2xl z-50 lg:hidden">
          <div className="grid grid-cols-3 h-18">
            <a href="tel:+35340454753" className="flex flex-col items-center justify-center py-3 hover:bg-[#2A2A2A] transition-colors">
              <i className="ri-phone-fill text-2xl text-[#00D084] mb-1"></i>
              <span className="text-white text-xs font-medium">Call</span>
            </a>
            <a href="https://wa.me/35340454753" className="flex flex-col items-center justify-center py-3 hover:bg-[#2A2A2A] transition-colors border-x border-gray-700">
              <i className="ri-whatsapp-fill text-2xl text-[#00D084] mb-1"></i>
              <span className="text-white text-xs font-medium">Message</span>
            </a>
            <Link to="/contact" className="flex flex-col items-center justify-center py-3 hover:bg-[#2A2A2A] transition-colors">
              <i className="ri-file-list-3-line text-2xl text-[#00D084] mb-1"></i>
              <span className="text-white text-xs font-medium">Quote</span>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
